import { useState } from "react";
import { nanoid } from "nanoid";
import { Survey, QuestionType } from "@/types/survey";
import { QuestionCard } from "./QuestionCard";
import { Button } from "@/components/ui/button";
import { Plus, FileText } from "lucide-react";

// ============================================
// Question Number Generation (Phase 1.4)
// ============================================

/**
 * Generate originalQuestionNumber for a new question added at the end.
 * Uses sequential numbering: Q1, Q2, Q3, etc.
 */
function generateQuestionNumber(existingQuestions: QuestionType[]): string {
  const nextNumber = existingQuestions.length + 1;
  return `Q${nextNumber}`;
}

/**
 * Generate originalQuestionNumber for a question inserted after another.
 * Uses suffix notation: Q3a, Q3b, Q3c, etc.
 */
function generateInsertedQuestionNumber(
  questions: QuestionType[],
  afterIndex: number
): string {
  const prevQuestion = questions[afterIndex];
  const prevNum = prevQuestion.originalQuestionNumber || `Q${afterIndex + 1}`;
  
  // Find existing suffixes for this base number
  // E.g., if Q3a and Q3b exist, next is Q3c
  const existingSuffixes = questions
    .filter(q => q.originalQuestionNumber?.startsWith(prevNum) && q.originalQuestionNumber !== prevNum)
    .map(q => (q.originalQuestionNumber ?? '').slice(prevNum.length))
    .filter(s => /^[a-z]$/.test(s));
  
  const nextSuffix = existingSuffixes.length === 0 
    ? 'a' 
    : String.fromCharCode(Math.max(...existingSuffixes.map(s => s.charCodeAt(0))) + 1);
  
  return `${prevNum}${nextSuffix}`;
}
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableQuestionCard } from "./SortableQuestionCard";

interface SurveyEditorProps {
  survey: Survey;
  onChange: (survey: Survey) => void;
}

export function SurveyEditor({ survey, onChange }: SurveyEditorProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleQuestionChange = (questionId: string, updatedQuestion: QuestionType) => {
    const updatedQuestions = survey.questions.map(q => 
      q.id === questionId ? updatedQuestion : q
    );
    onChange({ ...survey, questions: updatedQuestions });
  };

  const handleQuestionDelete = (questionId: string) => {
    const updatedQuestions = survey.questions.filter(q => q.id !== questionId);
    onChange({ ...survey, questions: updatedQuestions });
  };

  const handleQuestionDuplicate = (questionId: string) => {
    const questionToDuplicate = survey.questions.find(q => q.id === questionId);
    if (!questionToDuplicate) return;

    const questionIndex = survey.questions.findIndex(q => q.id === questionId);

    const newQuestion: QuestionType = {
      ...questionToDuplicate,
      id: nanoid(10),
      question: `${questionToDuplicate.question} (Copy)`,
      // Use suffix notation for inserted questions (Phase 1.4)
      // E.g., duplicating Q3 creates Q3a, Q3b, etc.
      originalQuestionNumber: generateInsertedQuestionNumber(survey.questions, questionIndex),
    };

    const updatedQuestions = [
      ...survey.questions.slice(0, questionIndex + 1),
      newQuestion,
      ...survey.questions.slice(questionIndex + 1),
    ];

    onChange({ ...survey, questions: updatedQuestions });
  };

  const handleAddQuestion = () => {
    const newQuestion: QuestionType = {
      id: nanoid(10),
      type: "open_text",
      question: "New Question",
      required: true,
      // AUTO-GENERATE for manual creation (Phase 1.4)
      originalQuestionNumber: generateQuestionNumber(survey.questions),
    };

    onChange({ 
      ...survey, 
      questions: [...survey.questions, newQuestion] 
    });
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = survey.questions.findIndex(q => q.id === active.id);
      const newIndex = survey.questions.findIndex(q => q.id === over.id);

      const newQuestions = arrayMove(survey.questions, oldIndex, newIndex);
      onChange({ ...survey, questions: newQuestions });
    }

    setActiveId(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const activeQuestion = activeId 
    ? survey.questions.find(q => q.id === activeId) 
    : null;

  return (
    <div className="space-y-4">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={survey.questions.map(q => q.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-4">
            {survey.questions.map((question, index) => (
              <SortableQuestionCard
                key={question.id}
                question={question}
                index={index}
                onChange={(updatedQuestion) => handleQuestionChange(question.id, updatedQuestion)}
                onDelete={() => handleQuestionDelete(question.id)}
                onDuplicate={() => handleQuestionDuplicate(question.id)}
                isDragging={activeId === question.id}
                surveyId={survey.id}
                allQuestions={survey.questions}
              />
            ))}
          </div>
        </SortableContext>
        
        {/* Drag Overlay - Ghost element while dragging */}
        <DragOverlay>
          {activeQuestion ? (
            <div className="opacity-90 shadow-xl">
              <QuestionCard
                question={activeQuestion}
                index={survey.questions.findIndex(q => q.id === activeQuestion.id)}
                onChange={() => {}}
                onDelete={() => {}}
                onDuplicate={() => {}}
                isDragging={true}
                surveyId={survey.id}
                allQuestions={survey.questions}
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      
      {/* Add Question Button */}
      <Button
        variant="outline"
        onClick={handleAddQuestion}
        className="w-full py-6 border-dashed border-2 hover:border-primary/50 hover:bg-muted/50 transition-colors"
      >
        <Plus className="h-5 w-5 mr-2" />
        Add Question
      </Button>
      
      {/* Empty State */}
      {survey.questions.length === 0 && (
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <FileText className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No questions yet</h3>
          <p className="text-muted-foreground mb-4">
            Add your first question to get started
          </p>
          <Button onClick={handleAddQuestion}>
            <Plus className="h-4 w-4 mr-2" />
            Add Question
          </Button>
        </div>
      )}
    </div>
  );
}
